import ChartPaper, {
    AXIS_LABEL_FONT_SIZE,
    CHART_HEIGHT,
    CHART_PADDING,
    estimateTableHeight,
    TABLE_HEADER_HEIGHT,
    TABLE_ROW_HEIGHT,
    truncateLabel
} from "./ChartPaper";
import { colours } from "../../../StyleConstants";
import { AgCharts } from "ag-charts-react";
import { AgChartOptions } from "ag-charts-community";
import { useMemo, useRef } from "react";
import { AgGridReact } from "@ag-grid-community/react";

type BarPoint = { label: string; value: number; description?: string };

// Hoisted so AG Grid does not re-apply its columns on every render of the dashlet.
const TABLE_COLUMN_DEFS = [{ field: 'label' }, { field: 'value' }];
const TABLE_AUTO_SIZE_STRATEGY = { type: 'fitGridWidth' } as const;

const MISSING_BAR_LABEL = "Missing";
// Keeps the missing-values bars visually separate from the histogram bins they share a chart with.
const MISSING_SPACER_LABEL = " ";

type Props = {
    data: BarPoint[];
    missingData: BarPoint[];
    title: string;
    subtitle?: string;
    table_name?: string;
    breakdownAvailable?: boolean;
    breakdownClicked?: (field: string) => void;
    ddAvailable: boolean;
    imagingData: any;
    eegData?: any;
    partialOptions?: Partial<AgChartOptions>;
    showAsTable?: boolean;
    showIssueOptions?: boolean;
}

const HistogramChart = (props: Props) => {
    const {
        data,
        missingData,
        title,
        subtitle,
        table_name,
        breakdownAvailable,
        breakdownClicked,
        ddAvailable,
        imagingData,
        eegData,
        partialOptions,
        showAsTable,
        showIssueOptions,
    } = props

    const gridRef = useRef<AgGridReact>(null);

    const toPrecision = (value: number, precision: number): number => {
        return parseFloat(value.toFixed(precision));
    }

    const binRanges: [number, number][] = useMemo(() => {
        let min: number = Math.min(...data.map((item) => parseFloat(item.label)));
        let max: number = Math.max(...data.map((item) => parseFloat(item.label)));

        const rawMin = min;
        const rawMax = max;
        const precision =
            (max - min < 0.01)
                ? 4 : (max - min) < 0.1
                    ? 3 : (max - min) < 1
                        ? 2  : 1;
        min = toPrecision(min, precision);
        max = toPrecision(max, precision);

        // Values that are (nearly) identical collapse to a zero-width range after rounding, which produced no
        // bins at all and therefore "No Rows To Show". Use a single bin around the actual values instead.
        if (max === min) {
            const lo = rawMin;
            const hi = rawMax > rawMin ? rawMax : rawMin + (Math.abs(rawMin) || 1) * 1e-3;
            return [[lo, hi]];
        }

        const binSize = (max - min) / 10;
        const bins: any = [];
        let currentBin = min;
        while (currentBin < max) {
            const nextBin = toPrecision(currentBin + binSize, precision);
            bins.push([currentBin, nextBin]);
            currentBin = nextBin;
        }

        return bins;
    }, [data]);

    const tableData = useMemo(() => {
        if (binRanges.length === 0) return [];
        const binnedData: any = {};
        data.forEach((item) => {
            if (item.value > 0) {
                const label = parseFloat(item.label);
                const toBin = binRanges.find((bin: any, index: number) =>
                    index < binRanges.length - 1
                        ? bin[0] <= label && bin[1] > label
                        : bin[0] <= label && bin[1] >= label
                ) ?? [0, 0];
                const key = `${toBin[0]}-${toBin[1]}`;
                if (!binnedData[key]) {
                    binnedData[key] = 0;
                }
                binnedData[key] += item.value;
            }
        })
        return Object.keys(binnedData).map((key) => ({
            label: key,
            value: binnedData[key]
        }));
    }, [binRanges])

    const rowData = useMemo(() => [...tableData, ...missingData], [tableData, missingData]);

    const visibleMissingData = useMemo(
        () => missingData.filter((row) => row.value > 0),
        [missingData]
    );

    // When there are missing values they are drawn in the same chart as the histogram (red bars after the bins),
    // so they share its axes instead of appearing as a separate graph.
    const combinedData = useMemo(() => {
        if (visibleMissingData.length === 0) return [];
        const counts = new Map(tableData.map((row: any) => [row.label, row.value]));
        const bins = binRanges.map(([from, to]) => ({
            label: `${from}-${to}`,
            value: counts.get(`${from}-${to}`) ?? 0,
            missing: false,
        }));
        const missing = visibleMissingData.map((row) => ({
            label: String(row.label ?? "").replace(/^unknown\s+/i, "") || MISSING_BAR_LABEL,
            value: row.value,
            missing: true,
        }));
        return [...bins, { label: MISSING_SPACER_LABEL, value: 0, missing: false }, ...missing];
    }, [binRanges, tableData, visibleMissingData]);

    return (
        <ChartPaper
            title={title}
            subtitle={subtitle}
            table_name={table_name}
            breakdownAvailable={breakdownAvailable}
            breakdownClicked={breakdownClicked}
            ddAvailable={ddAvailable}
            imagingData={imagingData}
            eegData={eegData}
            showIssueOptions={showIssueOptions}
            tableRows={rowData}
        >
            {showAsTable ?? false ?
                (<div
                    className={"ag-theme-alpine"}
                    style={{
                        width: '100%',
                        display: "block",
                        minHeight: `${estimateTableHeight(rowData.length)}px`,
                    }}
                >
                    <AgGridReact
                        ref={gridRef}
                        key={title}
                        rowData={rowData}
                        columnDefs={TABLE_COLUMN_DEFS}
                        domLayout={'autoHeight'}
                        rowHeight={TABLE_ROW_HEIGHT}
                        headerHeight={TABLE_HEADER_HEIGHT}
                        suppressAnimationFrame={true}
                        autoSizeStrategy={TABLE_AUTO_SIZE_STRATEGY}
                        suppressDragLeaveHidesColumns = {true}
                    />
                </div>)
                : visibleMissingData.length > 0 ? (
                    <AgCharts
                        style={{ height: `${CHART_HEIGHT}px`, contain: 'size layout', width: '100%', minWidth: 0 }}
                        key={`${title}-combined`}
                        options={{
                            ...partialOptions,
                            data: combinedData,
                            animation: { enabled: true },
                            padding: CHART_PADDING,
                            legend: { enabled: false },
                            axes: [
                                {
                                    type: "category",
                                    position: "bottom",
                                    paddingInner: 0.05,
                                    paddingOuter: 0.05,
                                    label: {
                                        fontSize: AXIS_LABEL_FONT_SIZE,
                                        formatter: ({ value }: { value?: any }) => truncateLabel(String(value ?? "")),
                                    },
                                },
                                {
                                    type: "number",
                                    position: "left",
                                    min: 0,
                                    label: { fontSize: AXIS_LABEL_FONT_SIZE },
                                },
                            ],
                            series: [
                                {
                                    type: "bar",
                                    xKey: "label",
                                    yKey: "value",
                                    yName: "",
                                    fill: colours.categorical.eight,
                                    itemStyler: ({ datum }: { datum: any }) => ({
                                        fill: datum?.missing ? colours.error.error : colours.categorical.eight,
                                    }),
                                    tooltip: {
                                        renderer: ({ datum }: { datum: any }) =>
                                            `<div style="padding:8px 12px"><strong>${String(datum?.label ?? "")}:</strong> ${datum?.value ?? ""}</div>`,
                                    },
                                },
                            ],
                        }}
                    />
                ) : (
                    <AgCharts
                        style={{ height: `${CHART_HEIGHT}px`, contain: 'size layout', width: '100%', minWidth: 0 }}
                        key={title}
                        options={{
                            ...partialOptions,
                            data: data,
                            animation: { enabled: true },
                            padding: CHART_PADDING,

                            axes: [
                                {
                                    type: "number",
                                    position: "bottom",
                                    label: {
                                        fontSize: AXIS_LABEL_FONT_SIZE
                                    },
                                },
                                {
                                    type: "number",
                                    position: "left",
                                    label: { fontSize: AXIS_LABEL_FONT_SIZE },
                                },
                            ],

                            series: [
                                {
                                    type:"histogram",
                                    xKey: "label",
                                    yKey: "value",
                                    xName: "label",
                                    yName: "",
                                    bins: binRanges,
                                    fill: colours.categorical.eight
                                },
                            ],
                        }}
                    />
                )
            }
        </ChartPaper>
    )
}

export default HistogramChart;
